/**
 * Модуль для работы с S3 API для Объектного хранилища.
 *
 * Примеры использования:
 *
 *  • PUT Bucket cors
 *
 *     import s3 from './s3';
 *
 *     s3('putBucketCors', {
 *         Bucket: 'bucket123',
 *         CORSConfiguration: {
 *             CORSRules: [
 *                 {
 *                   AllowedOrigins: ['*'],
 *                   AllowedHeaders: ['*'],
 *                   AllowedMethods: ['GET'],
 *                   MaxAgeSeconds: 365000
 *                 }
 *             ]
 *         }
 *     }).catch(response => {
 *         console.error(response.error);
 *     })
 *
 *  • GET Bucket cors
 *
 *     import s3 from './s3';
 *
 *     s3('getBucketCors', {Bucket: 'bucket123'}).then(response => {
 *         response.data.CORSRules[0].AllowedOrigins…
 *     });
 */

import AwsS3 from 'aws-sdk/clients/s3';
import { Request } from 'aws-sdk/lib/request';
import { Response } from 'aws-sdk/lib/response';
import { join } from 'storage/bucket-objects/utils/path';
import csrfToken from 'system/csrfToken';
import { ParallelTaskManager } from 'system/libs/ParallelTaskManager';
import { simpleXmlToJson } from 'system/libs/simpleXmlToJson';
import { uint8ArrayToString } from 'system/libs/uint8ArrayToString';
import { BackendTypes } from 'utils/SentryUtils';
import { HttpMethods } from 'utils/Types';
import { js2xml } from 'xml-js';

import { Environment } from 'Environment';
import { BoundCurrentUserSelectors } from 'reactApp/modules/CurrentUserModule';

import { SDKLogger } from '../system/SDKLogger';

export const PUBLIC_ENDPOINT = Environment.isStagingHost
    ? 'omega-hb.cldmail.ru'
    : 'hb.bizmrg.com';

export const MAX_KEYS = 10000;
const STORAGE_MAX_KEYS = 1000;
const MAX_PARALLEL_XHR_REQUESTS_PER_DOMAIN = 6;

let s3Client: AwsS3;

function createS3Client() {
    return new AwsS3({
        region: 'ru-msk',
        accessKeyId: 'MCSWEBS3',
        secretAccessKey: 'MCSWEBS3',
        s3ForcePathStyle: true,
        endpoint: s3.getPrivateEndpointUrl(),
    });
}

type CustomS3ReqOptions = {
    headers?: Record<string, string>;
    method?: HttpMethods;
    queryString?: string;
    hasCustomRequest?: boolean;
    getAllResponseData?: boolean;
};

/**
 * @url https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/S3.html
 */
export default function s3<T = any>(
    operation: keyof AwsS3,
    params: { Bucket?: string } & Record<string, any>,
    options: CustomS3ReqOptions = {},
) {
    return new Promise<T>((resolve, reject) => {
        if (!s3Client) {
            s3Client = createS3Client();
        }

        const request: Request<any, any> = options.hasCustomRequest
            ? getFakeRequest(params as any)
            : s3Client[operation as any](params);

        let requestLogger;

        request.on('build', (request) => {
            if (options.hasCustomRequest) {
                updateFakeRequest(request, params, options);
            }
            const httpRequest = request.httpRequest;

            // construct requestLogger here as here httpRequest gets correct `method` field
            requestLogger = SDKLogger.createSingleRequestLogger({
                group: 'storage',
                type: httpRequest.method,
                method: operation,
                // error response contains XML - don't need to log its raw contents
                logResponseTextOnError: false,
                backendType: BackendTypes.S3,
            });

            Object.assign(
                request.httpRequest.headers,
                {
                    'x-csrf-token': csrfToken.get('token'),
                },
                requestLogger.getLoggingHeaders(),
                options.headers,
            );
        });

        let loadProgress;
        request.on('httpUploadProgress', _updateLoadProgress);
        request.on('httpDownloadProgress', _updateLoadProgress);

        request.send((error) => {
            const response: Response<any, any> = (request as any).response;

            const httpResponse = response.httpResponse;

            const errorData = error
                ? {
                      s3ErrorCode: error.code,
                      s3RequestId: error.requestId,
                  }
                : null;

            requestLogger.logOnResponse({
                status: httpResponse.statusCode,
                responseText: httpResponse.body,

                getResponseHeader: (headerName) =>
                    httpResponse.headers[headerName],

                customData: {
                    ...loadProgress,
                    ...errorData,
                },
            });

            const completePromise = error ? reject : resolve;

            if (options.getAllResponseData) {
                const data = s3.getAllResponseData(response);
                completePromise(data);
            } else {
                completePromise(response as any);
            }
        });

        function _updateLoadProgress(progressEvent) {
            const { loaded, total } = progressEvent;

            loadProgress = { loaded, total };

            if (total !== 0) {
                loadProgress.percentage = Math.round((loaded / total) * 100);
            }
        }
    });
}

function getFakeRequest({ Bucket }: { Bucket: string }) {
    return s3Client.getBucketAcl({
        Bucket,
    });
}

function updateFakeRequest(
    { httpRequest }: Request<any, any>,
    params: any,
    options: CustomS3ReqOptions,
) {
    const { Bucket, ...restParams } = params;
    const mainParamKey = Object.keys(restParams)[0];
    restParams[mainParamKey] = {
        ...restParams[mainParamKey],
        _attributes: {
            xmlns: 'http://s3.amazonaws.com/doc/2006-03-01/',
        },
    };
    httpRequest.body = js2xml(
        {
            // TODO: find out does real api need this <?xml ... ?>
            // _declaration: {
            //     _attributes: {
            //         version: '1.0',
            //         encoding: 'UTF-8',
            //     },
            // },
            ...restParams,
        },
        {
            compact: true,
        },
    );

    httpRequest.method = options.method || httpRequest.method;

    if (options.queryString) {
        httpRequest.path = `${httpRequest.pathname()}?${options.queryString}`;
    }
}

/**
 * @param {string} operation
 * @param {Object} params
 * @returns {string}
 */
export function getSignedUrl(operation, params) {
    return s3Client.getSignedUrl(operation, params);
}

/**
 * @param {Object} params
 * @returns {Promise}
 */
export async function listObjects({ ...params }) {
    const { MaxKeys } = params;

    if (MaxKeys > STORAGE_MAX_KEYS) {
        params.MaxKeys = STORAGE_MAX_KEYS;
    }

    let contents = [];
    let prefixes = [];
    let response;

    while (contents.length + prefixes.length < MaxKeys) {
        response = await s3('listObjectsV2', params);

        const {
            data: {
                Contents,
                CommonPrefixes,
                IsTruncated,
                NextContinuationToken,
            },
        } = response;

        contents = contents.concat(Contents);
        prefixes = prefixes.concat(CommonPrefixes);

        if (IsTruncated) {
            params.ContinuationToken = NextContinuationToken;
        } else {
            delete params.ContinuationToken;

            break;
        }
    }

    response = {
        ...response,
        data: {
            ...response.data,
            Contents: contents,
            CommonPrefixes: prefixes,
            MaxKeys,
            KeyCount: contents.length + prefixes.length,
        },
    };

    return response;
}

function decodeS3URI(Key) {
    const key = Key.replace(/\+/g, '%20');

    try {
        return decodeURIComponent(key);
    } catch (reason) {
        return unescape(key);
    }
}

/**
 * @param {Object} params
 * @returns {Promise}
 */
export async function listObjectsWithNonEncodedKeys(params) {
    // An object key can contain any Unicode character.
    // However, XML 1.0 parsers cannot parse some characters,
    // such as characters with an ASCII value from 0 to 10.
    // For characters that are not supported in XML 1.0,
    // you can add this parameter to request.
    params.EncodingType = 'url';

    const response = await listObjects(params);
    const { CommonPrefixes, Contents } = response.data;

    // Выполняем decode ключей, так как нам больше нигде не должен понадобиться формат Amazon.
    for (const Content of Contents) {
        Content.Key = decodeS3URI(Content.Key);
    }

    for (const CommonPrefix of CommonPrefixes) {
        CommonPrefix.Prefix = decodeS3URI(CommonPrefix.Prefix);
    }

    return response;
}

/**
 * @param {string} operation
 * @param {string} params.Bucket
 * @param {string} params.Prefix
 * @returns {Promise}
 */
s3.applyOperationToObjectsByPrefix = async function(
    operation,
    { Bucket, Prefix, ...operationParams },
) {
    let isTruncated = true;
    const listingParams = { Bucket, Prefix, MaxKeys: STORAGE_MAX_KEYS };

    while (isTruncated) {
        const {
            data: { Contents, IsTruncated, NextContinuationToken },
        } = await listObjectsWithNonEncodedKeys(listingParams);

        isTruncated = IsTruncated;
        // @ts-ignore
        listingParams.ContinuationToken = NextContinuationToken;

        await ParallelTaskManager.all({
            // @ts-ignore
            eachTaskParams: Contents,
            taskFactory: ({ Key }) =>
                s3(operation, { Bucket, Key, ...operationParams }),
            maxParallelTaskCount: MAX_PARALLEL_XHR_REQUESTS_PER_DOMAIN,
        });
    }
};

s3.listBuckets = async function(params, options: any = {}) {
    const response = await s3('listBuckets', params, {
        ...options,
        headers: {
            ...options.headers,
            'x-mcs-attr': 'storage-class,read-only',
        },
    });

    const {
        data: { Buckets = [] },
    } = response;

    if (Buckets.length) {
        let buckets = s3.getAllResponseData(response).Buckets.Bucket;

        if (buckets && !Array.isArray(buckets)) {
            buckets = [buckets];
        }

        let i = buckets.length;

        while (i--) {
            Buckets[i].StorageClass = buckets[i].StorageClass;
            Buckets[i].ReadOnly = buckets[i].ReadOnly === 'true';
        }
    }

    return response;
};

s3.getBucketInfo = async function({ Bucket, ...params }, options) {
    const response = await s3.listBuckets(params, options);
    const { data } = response;
    const bucket = data.Buckets.find(({ Name }) => Name === Bucket);

    if (bucket) {
        delete data.Buckets;

        data.Bucket = bucket;

        return response;
    }

    throw {
        error: {
            code: 'NotFound',
            statusCode: 404,
        },
    };
};

/**
 * @param {AWS.Response} response
 * @param {AWS.HttpResponse} response.httpResponse
 * @param {Object} response.data
 * @returns {Promise}
 */
s3.getAllResponseData = function({ httpResponse, data }) {
    try {
        const responseText = uint8ArrayToString(httpResponse.body);
        const responseXml = new DOMParser().parseFromString(
            responseText,
            'text/xml',
        );

        data = simpleXmlToJson(responseXml.children[0]);
    } catch (reason) {
        // nothing to do
    }

    return data;
};

s3.getPrivateEndpointUrl = () =>
    join(
        window.location.origin,
        '/api/s3/hotbox',
        BoundCurrentUserSelectors.getPid(),
    );

export const PUBLIC_GRANT = {
    Permission: 'READ',
    Grantee: {
        Type: 'Group',
        URI: 'http://acs.amazonaws.com/groups/global/AllUsers',
    },
};

export const ACL_PUBLIC_READ = 'public-read';
export const ACL_PRIVATE = 'private';

export const STORAGE_CLASS_STANDARD = 'STANDARD';
export const STORAGE_CLASS_STANDARD_IA = 'STANDARD_IA';
export const STORAGE_CLASS_BACKUP = 'BACKUP';

export const STORAGE_CLASS_NAMES = {
    [STORAGE_CLASS_STANDARD]: 'Hotbox',
    [STORAGE_CLASS_STANDARD_IA]: 'Icebox', // Infrequent Access
    [STORAGE_CLASS_BACKUP]: 'Backup',
};
